import React from 'react';
import { Phone, Mail, MapPin, Clock, Instagram } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Contact = () => {
  // Composant pour logo TikTok
  const TikTokIcon = ({ className }) => (
    <img 
      src="https://customer-assets.emergentagent.com/job_car-detailing-9/artifacts/lglcneuc_logo-tik-tok_578229-290.avif" 
      alt="TikTok" 
      className={`${className} object-contain`}
      style={{mixBlendMode: 'screen', opacity: 0.9}}
    />
  );

  // Composant pour logo Snapchat
  const SnapchatIcon = ({ className }) => (
    <img 
      src="https://customer-assets.emergentagent.com/job_car-detailing-9/artifacts/iyt2vivk_Snapchat.png" 
      alt="Snapchat" 
      className={`${className} object-contain`}
      style={{mixBlendMode: 'multiply', opacity: 1}}
    />
  );

  const contactMethods = [
    {
      name: 'Instagram',
      icon: Instagram,
      color: 'from-purple-600 to-pink-600',
      hoverColor: 'hover:shadow-purple-500/50',
      info: '@auto_clean91',
      link: 'https://www.instagram.com/auto_clean91/',
      description: 'Suivez nos réalisations et actualités'
    },
    {
      name: 'Snapchat',
      icon: SnapchatIcon,
      color: 'from-yellow-400 to-yellow-600',
      hoverColor: 'hover:shadow-yellow-500/50',
      info: '@auto_clean91',
      link: 'https://www.snapchat.com/add/auto_clean91',
      description: 'Contenus exclusifs et promotions'
    },
    {
      name: 'TikTok',
      icon: TikTokIcon,
      color: 'from-black to-blue-600',
      hoverColor: 'hover:shadow-blue-500/50',
      info: '@auto_clean91',
      link: 'https://www.tiktok.com/@auto_clean91',
      description: 'Découvrez nos vidéos et astuces'
    },
    {
      name: 'Téléphone',
      icon: Phone,
      color: 'from-blue-600 to-blue-800',
      hoverColor: 'hover:shadow-blue-500/50',
      info: '07 66 84 55 19',
      link: 'tel:0766845519',
      description: 'Appelez-nous pour un rendez-vous rapide'
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'from-cyan-600 to-cyan-800',
      hoverColor: 'hover:shadow-cyan-500/50',
      info: 'entreprise.auto.clean@gmail.com',
      link: 'mailto:entreprise.auto.clean@gmail.com',
      description: 'Envoyez-nous un message détaillé'
    },
    {
      name: 'Adresse',
      icon: MapPin,
      color: 'from-green-600 to-green-800',
      hoverColor: 'hover:shadow-green-500/50',
      info: '107 Avenue François Mitterrand, Athis-Mons',
      link: 'https://maps.google.com/?q=107+Avenue+François+Mitterrand+Athis-Mons',
      description: 'Rendez-vous directement chez nous'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-950 to-black">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
            Nous <span className="text-blue-500">Contacter</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto animate-slide-up">
            Une question ? Un rendez-vous ? N'hésitez pas à nous contacter par le moyen qui vous convient le mieux !
          </p>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section className="py-16 px-4 bg-black">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contactMethods.map((contact, index) => (
              <a 
                key={index}
                href={contact.link}
                target={contact.name === 'Adresse' ? '_blank' : '_self'}
                rel={contact.name === 'Adresse' ? 'noopener noreferrer' : ''}
                className="block"
              >
                <Card 
                  className="relative bg-gray-900/50 border-blue-500/30 p-8 hover:bg-gray-900/70 transition-all duration-300 hover:scale-105 cursor-pointer group overflow-hidden h-full"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${contact.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <div className="relative z-10">
                    <div className="flex justify-center mb-6">
                      <div className={`p-4 rounded-full bg-gradient-to-br ${contact.color} group-hover:scale-110 transition-transform duration-300`}>
                        <contact.icon className="h-12 w-12 text-white" />
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-center mb-3 text-white">
                      {contact.name}
                    </h3>
                    
                    <p className="text-blue-400 text-center font-medium mb-4 break-words px-2">
                      {contact.info}
                    </p>
                    
                    <p className="text-gray-400 text-center text-sm">
                      {contact.description}
                    </p>
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-black to-gray-950">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-full bg-gradient-to-br from-blue-600 to-blue-800">
                <Clock className="h-10 w-10 text-white" />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="text-blue-500">Uniquement sur rendez-vous</span>
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Contactez-nous pour fixer un rendez-vous selon vos disponibilités
            </p>
          </div>

          <Card className="bg-gray-900/50 border-blue-500/30 p-8">
            <h3 className="text-xl font-bold text-center mb-6 text-white">Pourquoi prendre rendez-vous ?</h3>
            <ul className="space-y-4">
              <li className="flex items-start text-gray-300">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span>Service personnalisé et dédié à votre véhicule</span>
              </li>
              <li className="flex items-start text-gray-300">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span>Pas d'attente, nous sommes prêts pour vous</span>
              </li>
              <li className="flex items-start text-gray-300">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span>Flexibilité selon votre emploi du temps</span>
              </li>
              <li className="flex items-start text-gray-300">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span>Conseils et devis personnalisé sur place</span>
              </li>
            </ul>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-950">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à redonner vie à votre véhicule ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Réservez dès maintenant votre rendez-vous et profitez d'un service de qualité professionnelle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0766845519">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/50">
                <Phone className="mr-2 h-5 w-5" />
                Appeler maintenant
              </Button>
            </a>
            <a href="mailto:entreprise.auto.clean@gmail.com">
              <Button className="bg-white hover:bg-gray-100 text-black px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl">
                <Mail className="mr-2 h-5 w-5" />
                Envoyer un email
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
